import { hasPermission } from '@ctrl/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { SwitchField, TextField } from '@/components/form-fields';
import {
  AddButton,
  EmptyState,
  ListRow,
  ListShell,
  ReadOnlyNotice,
  SectionCard,
} from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth';
import {
  deleteTier,
  getLoyaltyProgram,
  type LoyaltyProgram,
  type LoyaltyTier,
  listMembers,
  listTiers,
  saveLoyaltyProgram,
  saveTier,
} from '@/lib/retention';
import { useForm } from '@/lib/use-form';

/**
 * The loyalty programme.
 *
 * Earning and redeeming are both expressed as ratios rather than rates,
 * because that is how a shop describes them out loud: a point per dollar,
 * a dollar per hundred points. The pricing pipeline reads exactly these
 * numbers when it clamps a redemption, so what is typed here is what a
 * shopper can take off.
 */
export const Route = createFileRoute('/loyalty/')({
  component: LoyaltyPage,
});

const schema = z.object({
  enabled: z.boolean(),
  programName: z.string().trim().min(1, 'Name the programme').max(60),
  pointsName: z.string().trim().min(1, 'What are they called?').max(30),
  earnDollars: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,2})?$/, 'Dollars per point, e.g. 1.00'),
  redeemPoints: z
    .string()
    .trim()
    .regex(/^\d{1,6}$/, 'A whole number'),
  redeemDollars: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,2})?$/, 'Dollars, e.g. 1.00'),
  minRedeemPoints: z
    .string()
    .trim()
    .regex(/^\d{1,6}$/, 'A whole number'),
  maxRedeemPercent: z
    .string()
    .trim()
    .regex(/^\d{1,3}$/, 'A percentage'),
  pointsExpireDays: z.string().trim().regex(/^\d*$/, 'Days, or blank for never'),
});

type Values = z.infer<typeof schema>;

function toValues(program: LoyaltyProgram): Values {
  return {
    enabled: program.enabled,
    programName: program.programName,
    pointsName: program.pointsName,
    earnDollars: (program.earnCentsPerPoint / 100).toFixed(2),
    redeemPoints: String(program.redeemPoints),
    redeemDollars: (program.redeemCents / 100).toFixed(2),
    minRedeemPoints: String(program.minRedeemPoints),
    maxRedeemPercent: String(program.maxRedeemPercent),
    pointsExpireDays: program.pointsExpireDays == null ? '' : String(program.pointsExpireDays),
  };
}

function LoyaltyPage() {
  const { activeAccountId, claims } = useAuth();
  const accountId = activeAccountId ?? '';
  const queryClient = useQueryClient();

  const canManage = accountId ? hasPermission(claims, accountId, 'loyalty.manage') : false;

  const program = useQuery({
    queryKey: ['loyalty-program', accountId],
    queryFn: () => getLoyaltyProgram(accountId),
    enabled: Boolean(accountId),
  });

  const tiers = useQuery({
    queryKey: ['loyalty-tiers', accountId],
    queryFn: () => listTiers(accountId),
    enabled: Boolean(accountId),
  });

  const members = useQuery({
    queryKey: ['loyalty-members', accountId],
    queryFn: () => listMembers(accountId),
    enabled: Boolean(accountId),
  });

  const { values, errors, isDirty, set, reset, submit } = useForm<Values>(
    toValues(program.data ?? ({} as LoyaltyProgram)),
    schema,
  );

  useEffect(() => {
    if (program.data) reset(toValues(program.data));
  }, [program.data, reset]);

  const save = useMutation({
    mutationFn: (parsed: Values) =>
      saveLoyaltyProgram(accountId, {
        enabled: parsed.enabled,
        programName: parsed.programName,
        pointsName: parsed.pointsName,
        earnCentsPerPoint: Math.round(Number(parsed.earnDollars) * 100),
        redeemPoints: Number(parsed.redeemPoints),
        redeemCents: Math.round(Number(parsed.redeemDollars) * 100),
        minRedeemPoints: Number(parsed.minRedeemPoints),
        maxRedeemPercent: Number(parsed.maxRedeemPercent),
        pointsExpireDays: parsed.pointsExpireDays === '' ? null : Number(parsed.pointsExpireDays),
      }),
    onSuccess: () => {
      toast.success('Programme saved.');
      queryClient.invalidateQueries({ queryKey: ['loyalty-program', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (program.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Loyalty</h1>
        <p className="text-muted-foreground text-sm">
          {program.data?.enabled
            ? `${program.data.programName} is running`
            : 'Not running — shoppers earn nothing yet'}
        </p>
      </div>

      {!canManage && <ReadOnlyNotice permission="loyalty.manage" />}

      <form onSubmit={submit((parsed) => save.mutate(parsed))} noValidate className="space-y-6">
        <fieldset disabled={!canManage} className="space-y-6">
          <SectionCard
            title="Earning and redeeming"
            description="Read at checkout when a shopper spends points: the minimum, the ratio and the ceiling are all enforced there."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="loyalty-name"
                label="Programme name"
                value={values.programName}
                error={errors.programName}
                onChange={(value) => set('programName', value)}
              />
              <TextField
                id="loyalty-points-name"
                label="What points are called"
                placeholder="points"
                value={values.pointsName}
                error={errors.pointsName}
                onChange={(value) => set('pointsName', value)}
              />
              <TextField
                id="loyalty-earn"
                label="Dollars spent per point"
                description="1.00 means a point for every dollar."
                value={values.earnDollars}
                error={errors.earnDollars}
                onChange={(value) => set('earnDollars', value)}
              />
              <div className="grid grid-cols-2 gap-3">
                <TextField
                  id="loyalty-redeem-points"
                  label="Points"
                  description="Worth…"
                  value={values.redeemPoints}
                  error={errors.redeemPoints}
                  onChange={(value) => set('redeemPoints', value)}
                />
                <TextField
                  id="loyalty-redeem-dollars"
                  label="Dollars"
                  description="…this much off."
                  value={values.redeemDollars}
                  error={errors.redeemDollars}
                  onChange={(value) => set('redeemDollars', value)}
                />
              </div>
              <TextField
                id="loyalty-min"
                label="Fewest points redeemable"
                value={values.minRedeemPoints}
                error={errors.minRedeemPoints}
                onChange={(value) => set('minRedeemPoints', value)}
              />
              <TextField
                id="loyalty-max"
                label="Most of an order points can cover"
                description="Percent."
                value={values.maxRedeemPercent}
                error={errors.maxRedeemPercent}
                onChange={(value) => set('maxRedeemPercent', value)}
              />
              <TextField
                id="loyalty-expiry"
                label="Points expire after"
                description="Days. Blank means never."
                value={values.pointsExpireDays}
                error={errors.pointsExpireDays}
                onChange={(value) => set('pointsExpireDays', value)}
              />
            </div>

            <SwitchField
              id="loyalty-enabled"
              label="Programme is running"
              description="Off, nothing is earned and nothing can be redeemed. Balances are kept."
              checked={values.enabled}
              onChange={(checked) => set('enabled', checked)}
            />

            <div className="flex gap-2">
              <Button type="submit" disabled={!isDirty || save.isPending}>
                {save.isPending ? 'Saving…' : 'Save programme'}
              </Button>
              {isDirty && (
                <Button type="button" variant="ghost" onClick={() => reset()}>
                  Discard
                </Button>
              )}
            </div>
          </SectionCard>
        </fieldset>
      </form>

      <TiersPanel accountId={accountId} canManage={canManage} tiers={tiers.data ?? []} />

      <SectionCard
        title="Members"
        description="Everyone holding a balance, richest first. A member's own history is on their customer record."
      >
        {members.data?.length ? (
          <ListShell>
            {members.data.map((member) => (
              <ListRow
                key={member.customerId}
                title={
                  <Link
                    to="/customers/$customerId"
                    params={{ customerId: member.customerId }}
                    className="hover:underline"
                  >
                    {member.name}
                  </Link>
                }
                subtitle={`${member.lifetimeEarned} earned · ${member.lifetimeRedeemed} redeemed`}
                badges={
                  <Badge variant="secondary">
                    {member.balancePoints} {program.data?.pointsName ?? 'points'}
                  </Badge>
                }
              />
            ))}
          </ListShell>
        ) : (
          <EmptyState>Nobody has earned anything yet. Points accrue as orders complete.</EmptyState>
        )}
      </SectionCard>
    </div>
  );
}

function TiersPanel({
  accountId,
  canManage,
  tiers,
}: {
  accountId: string;
  canManage: boolean;
  tiers: LoyaltyTier[];
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({ name: '', threshold: '500', multiplier: '1.25' });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['loyalty-tiers', accountId] });

  const add = useMutation({
    mutationFn: () =>
      saveTier(accountId, {
        name: draft.name.trim(),
        thresholdPointsLifetime: Number(draft.threshold),
        multiplierBps: Math.round(Number(draft.multiplier) * 10_000),
        benefits: [],
      }),
    onSuccess: () => {
      setDraft({ name: '', threshold: '500', multiplier: '1.25' });
      toast.success('Tier added.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteTier(accountId, id),
    onSuccess: refresh,
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <SectionCard
      title="Tiers"
      description="Reached on lifetime points earned. The multiplier changes how fast a member earns from then on."
    >
      {tiers.length ? (
        <ListShell>
          {tiers.map((tier) => (
            <ListRow
              key={tier.id}
              title={tier.name}
              subtitle={`From ${tier.thresholdPointsLifetime} lifetime points · earns ${(
                tier.multiplierBps / 10_000
              ).toFixed(2)}×`}
              actions={
                canManage ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => remove.mutate(tier.id)}
                  >
                    Remove
                  </Button>
                ) : null
              }
            />
          ))}
        </ListShell>
      ) : (
        <EmptyState>No tiers: everybody earns at the same rate.</EmptyState>
      )}

      {canManage && (
        <div className="flex flex-wrap items-end gap-3">
          <TextField
            id="tier-name"
            label="Name"
            className="w-40"
            placeholder="Gold"
            value={draft.name}
            onChange={(value) => setDraft((current) => ({ ...current, name: value }))}
          />
          <TextField
            id="tier-threshold"
            label="Lifetime points"
            className="w-36"
            value={draft.threshold}
            onChange={(value) => setDraft((current) => ({ ...current, threshold: value }))}
          />
          <TextField
            id="tier-multiplier"
            label="Earn multiplier"
            className="w-36"
            value={draft.multiplier}
            onChange={(value) => setDraft((current) => ({ ...current, multiplier: value }))}
          />
          <AddButton onClick={() => add.mutate()} disabled={!draft.name.trim()}>
            Add tier
          </AddButton>
        </div>
      )}
    </SectionCard>
  );
}
